import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../services/api';
import { useInactivityLogout } from '../hooks/useInactivityLogout';
import { UserRoleContext, computePermissions } from '../hooks/useUserRole';
import { User } from '@dsgvo-buddy/types';
import {
  ShellBar,
  Avatar,
  SideNavigation,
  SideNavigationItem,
  FlexBox
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';

interface AppLayoutProps {
  children: ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<(User & { organization_role?: string; plan?: string }) | null>(null);
  const [collapsed] = useState(false);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    const response = await authApi.getCurrentUser();
    if (response.success && response.data) {
      setUser(response.data);
    }
  };

  const permissions = computePermissions(user?.organization_role);
  const isOwner = permissions.isOwner;

  const handleLogout = useCallback(() => {
    authApi.logout();
    navigate('/login');
  }, [navigate]);

  useInactivityLogout(handleLogout);

  const currentPath = location.pathname;

  const getPageTitle = () => {
    if (currentPath === '/dashboard') return 'Dashboard';
    if (currentPath === '/onboarding') return 'Datenschutz-Kompass';
    if (currentPath === '/organization') return 'Organisation';
    if (currentPath.startsWith('/processing/new')) return 'Neue Verarbeitung';
    if (currentPath.match(/\/processing\/\d+\/edit/)) return 'Verarbeitung bearbeiten';
    if (currentPath.match(/\/processing\/\d+/)) return 'Verarbeitungsdetails';
    if (currentPath === '/processing') return 'Verarbeitungstätigkeiten';
    if (currentPath.startsWith('/service-providers/new')) return 'Neuer Dienstleister';
    if (currentPath.match(/\/service-providers\/\d+\/edit/)) return 'Dienstleister bearbeiten';
    if (currentPath.match(/\/service-providers\/\d+/)) return 'Dienstleister-Details';
    if (currentPath === '/service-providers') return 'Dienstleister & AVV';
    if (currentPath.startsWith('/tom/new')) return 'Neue TOM-Maßnahme';
    if (currentPath.match(/\/tom\/\d+\/edit/)) return 'TOM-Maßnahme bearbeiten';
    if (currentPath.match(/\/tom\/\d+/)) return 'TOM-Details';
    if (currentPath === '/tom') return 'TOM-Maßnahmen';
    if (currentPath.startsWith('/privacy-notices/new')) return 'Neuer Datenschutzhinweis';
    if (currentPath.match(/\/privacy-notices\/\d+\/edit/)) return 'Datenschutzhinweis bearbeiten';
    if (currentPath.match(/\/privacy-notices\/\d+/)) return 'Datenschutzhinweis-Details';
    if (currentPath === '/privacy-notices') return 'Datenschutzhinweise';
    if (currentPath.startsWith('/data-subject-requests/new')) return 'Neue Betroffenenanfrage';
    if (currentPath.match(/\/data-subject-requests\/\d+\/edit/)) return 'Anfrage bearbeiten';
    if (currentPath.match(/\/data-subject-requests\/\d+/)) return 'Anfrage-Details';
    if (currentPath === '/data-subject-requests') return 'Betroffenenrechte';
    if (currentPath.startsWith('/breaches/new')) return 'Neuen Vorfall melden';
    if (currentPath.match(/\/breaches\/\d+\/edit/)) return 'Vorfall bearbeiten';
    if (currentPath.match(/\/breaches\/\d+/)) return 'Vorfall-Details';
    if (currentPath === '/breaches') return 'Datenschutzvorfälle';
    if (currentPath === '/reports') return 'Berichte und Export';
    if (currentPath === '/import') return 'JSON-Import';
    if (currentPath === '/audit-log') return 'Audit-Protokoll';
    if (currentPath === '/users') return 'Benutzerverwaltung';
    if (currentPath === '/billing') return 'Abonnement';
    return '';
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <ShellBar
        primaryTitle="DSGVOeasy"
        secondaryTitle={getPageTitle()}
        profile={
          <Avatar
            size="XS"
            initials={user?.name?.substring(0, 2).toUpperCase() || 'U'}
            colorScheme="Accent6"
          />
        }
        onProfileClick={handleLogout}
        showCoPilot={false}
        logo={<img src="/logo.png" alt="DSGVOeasy" style={{ height: '2rem', width: 'auto' }} />}
      />

      <FlexBox style={{ flex: 1, overflow: 'hidden' }}>
        <SideNavigation
          className="app-sidebar"
          collapsed={collapsed}
          style={{ height: '100%' }}
          onSelectionChange={(e: any) => {
            const item = e.detail.item;
            if (item?.dataset?.route) {
              navigate(item.dataset.route);
            }
          }}
        >
          <SideNavigationItem
            text="Dashboard"
            icon="home"
            data-route="/dashboard"
            selected={currentPath === '/dashboard'}
          />
          <SideNavigationItem
            text="Datenschutz-Kompass"
            icon="compass"
            data-route="/onboarding"
            selected={currentPath === '/onboarding'}
          />
          <SideNavigationItem
            text="Organisation"
            icon="factory"
            data-route="/organization"
            selected={currentPath === '/organization'}
          />
          <SideNavigationItem
            text="Verarbeitungen"
            icon="documents"
            data-route="/processing"
            selected={currentPath.startsWith('/processing')}
          />
          <SideNavigationItem
            text="Dienstleister"
            icon="supplier"
            data-route="/service-providers"
            selected={currentPath.startsWith('/service-providers')}
          />
          <SideNavigationItem
            text="TOM-Maßnahmen"
            icon="shield"
            data-route="/tom"
            selected={currentPath.startsWith('/tom')}
          />
          <SideNavigationItem
            text="Datenschutzhinweise"
            icon="letter"
            data-route="/privacy-notices"
            selected={currentPath.startsWith('/privacy-notices')}
          />
          <SideNavigationItem
            text="Betroffenenrechte"
            icon="person-placeholder"
            data-route="/data-subject-requests"
            selected={currentPath.startsWith('/data-subject-requests')}
          />
          <SideNavigationItem
            text="Vorfälle"
            icon="alert"
            data-route="/breaches"
            selected={currentPath.startsWith('/breaches')}
          />
          <SideNavigationItem
            text="Berichte"
            icon="print"
            data-route="/reports"
            selected={currentPath === '/reports'}
          />
          <SideNavigationItem
            text="Import"
            icon="upload"
            data-route="/import"
            selected={currentPath === '/import'}
          />
          <SideNavigationItem
            text="Export"
            icon="download"
            data-route="/export"
            selected={currentPath === '/export'}
          />
          <SideNavigationItem
            text="Audit-Protokoll"
            icon="activity-2"
            data-route="/audit-log"
            selected={currentPath === '/audit-log'}
          />
          {isOwner && (
            <SideNavigationItem
              text="Benutzerverwaltung"
              icon="employee"
              data-route="/users"
              selected={currentPath === '/users'}
            />
          )}
          {isOwner && (
            <SideNavigationItem
              text="Abonnement"
              icon="cart"
              data-route="/billing"
              selected={currentPath === '/billing'}
            />
          )}
        </SideNavigation>

        <div className="app-content" style={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: 'var(--sapBackgroundColor)',
          padding: '1.5rem 2rem'
        }}>
          <UserRoleContext.Provider value={permissions}>
            {children}
          </UserRoleContext.Provider>
        </div>
      </FlexBox>
    </div>
  );
}
